import Phaser from 'phaser';

const FALL_DURATION_MS = 280;

export class ArrowManager {
  constructor(scene) {
    this.scene = scene;
    this.arrows = [];
  }

  shoot({ shooter, target, damage, speed = 720, maxDistance = null, spriteKey = null }) {
    if (!this.scene || !shooter?.sprite || !target?.sprite) return null;

    const startX = shooter.sprite.x;
    const startY = shooter.sprite.y;
    const targetX = target.sprite.x;
    const targetY = target.sprite.y;
    const angle = Phaser.Math.Angle.Between(startX, startY, targetX, targetY);

    // Use provided spriteKey, or try to get it from shooter's arrow item, or fallback to default
    let arrowSpriteKey = spriteKey || 'arrow-projectile';
    if (!spriteKey && shooter && typeof shooter.getArrowItemIconKey === 'function') {
      const iconKey = shooter.getArrowItemIconKey();
      if (iconKey && this.scene.textures && this.scene.textures.exists(iconKey)) {
        arrowSpriteKey = iconKey;
      } else if (!this.scene.textures || !this.scene.textures.exists('arrow-projectile')) {
        // If the arrow item's iconKey doesn't exist, and default doesn't exist either,
        // try to find any arrow-related texture as last resort
        const arrowItem = shooter?.inventory?.find(item => 
          item.itemType === 'ammo' || item.itemType === 'arrow' ||
          shooter.scene?.itemsById?.get(item.itemId)?.type === 'ammo' ||
          shooter.scene?.itemsById?.get(item.itemId)?.type === 'arrow'
        );
        if (arrowItem) {
          const itemDef = shooter.scene?.itemsById?.get(arrowItem.itemId);
          const fallbackIconKey = itemDef?.iconKey;
          if (fallbackIconKey && this.scene.textures.exists(fallbackIconKey)) {
            arrowSpriteKey = fallbackIconKey;
          }
        }
      }
    }

    const sprite = this.scene.add.image(startX, startY, arrowSpriteKey);
    const scale = (typeof this.scene.objectScale === 'number' && this.scene.objectScale > 0)
      ? this.scene.objectScale
      : 1;
    sprite.setScale(0.35 * scale);
    sprite.setDepth(6);
    // Arrow art points 45 degrees clockwise from vertical (up)
    // Phaser angles: 0 = right, π/2 = up, π = left, 3π/2 = down
    // If sprite points 45° clockwise from up, that's π/2 - π/4 = π/4 radians (northeast)
    // Original code assumed sprite points up (π/2), so used: angle + π/2
    // Since sprite actually points at π/4 instead of π/2, we subtract the difference: π/2 - π/4 = π/4
    sprite.setRotation(angle + Math.PI / 2 - Math.PI / 4);

    const normalizedMaxDistance = Number.isFinite(maxDistance) ? Math.max(0, maxDistance) : null;
    const arrow = {
      shooter,
      sprite,
      damage,
      velocityX: Math.cos(angle) * speed,
      velocityY: Math.sin(angle) * speed,
      hitRadius: 28 * scale,
      createdAt: this.scene.time?.now || Date.now(),
      lifetime: normalizedMaxDistance && speed > 0 ? Math.max(2000, (normalizedMaxDistance / speed) * 1200) : 2600,
      angle,
      hasHit: false,
      maxDistance: normalizedMaxDistance,
      distanceTravelled: 0,
    };

    this.arrows.push(arrow);
    return arrow;
  }

  update(delta) {
    if (!this.arrows.length) return;
    const dt = delta / 1000;
    const now = this.scene.time?.now || Date.now();
    const bounds = this.scene.gameManager?.worldBounds || null;

    this.arrows = this.arrows.filter((arrow) => {
      if (!arrow.sprite || !arrow.sprite.active) {
        return false;
      }

      const moveX = arrow.velocityX * dt;
      const moveY = arrow.velocityY * dt;
      arrow.sprite.x += moveX;
      arrow.sprite.y += moveY;
      arrow.distanceTravelled += Math.hypot(moveX, moveY);

      if (arrow.maxDistance && arrow.distanceTravelled >= arrow.maxDistance) {
        this.destroyArrow(arrow, { fall: true });
        return false;
      }

      if (this.checkPlayerHit(arrow)) {
        this.destroyArrow(arrow, { immediate: true });
        return false;
      }

      if (!this.isInsideBounds(arrow.sprite.x, arrow.sprite.y, bounds) || now - arrow.createdAt > arrow.lifetime) {
        this.destroyArrow(arrow, { fall: true });
        return false;
      }

      return true;
    });
  }

  checkPlayerHit(arrow) {
    const players = this.scene.players || [];
    const hitRadiusSq = Math.pow(arrow.hitRadius, 2);

    for (const target of players) {
      if (!target || !target.isAlive || target === arrow.shooter) continue;
      if (typeof arrow.shooter.canAttack === 'function' && !arrow.shooter.canAttack(target)) continue;
      if (!target.sprite) continue;

      const dx = target.sprite.x - arrow.sprite.x;
      const dy = target.sprite.y - arrow.sprite.y;
      if ((dx * dx + dy * dy) <= hitRadiusSq) {
        const damage = Math.max(1, arrow.damage || 1);
        const willDie = target.health <= damage;
        const dealt = target.takeDamage(damage);
        if (Number.isFinite(dealt) && dealt > 0) {
          arrow.shooter.damageDealt = (arrow.shooter.damageDealt || 0) + dealt;
          if (typeof arrow.shooter.handleDamageDealtForUltimate === 'function') {
            arrow.shooter.handleDamageDealtForUltimate(dealt);
          }
          if (this.scene.sfxManager) {
            if (typeof this.scene.sfxManager.onAttack === 'function') {
              this.scene.sfxManager.onAttack(arrow.shooter);
            }
            if (typeof this.scene.sfxManager.onHit === 'function') {
              this.scene.sfxManager.onHit(target, dealt, willDie);
            }
          }
          if (typeof this.scene.createAttackEffect === 'function') {
            this.scene.createAttackEffect(arrow.shooter, target);
          }
          if (!target.isAlive) {
            arrow.shooter.addElimination();
            if (this.scene.createDeathMarker && target.sprite) {
              this.scene.createDeathMarker(target.sprite.x, target.sprite.y, { popScale: target.sprite.scale * 10 });
            }
            if (this.scene.sfxManager && typeof this.scene.sfxManager.onDeath === 'function') {
              this.scene.sfxManager.onDeath(target);
            }
          }
        }
        return true;
      }
    }

    return false;
  }

  isInsideBounds(x, y, bounds) {
    if (!bounds) return true;
    return (
      x >= bounds.x - 20 &&
      x <= bounds.x + bounds.width + 20 &&
      y >= bounds.y - 20 &&
      y <= bounds.y + bounds.height + 20
    );
  }

  destroyArrow(arrow, { immediate = false, fall = false } = {}) {
    if (!arrow.sprite) return;
    const sprite = arrow.sprite;
    arrow.sprite = null;

    if (immediate) {
      sprite.destroy();
      return;
    }

    if (fall) {
      const dropDistance = 24 * ((typeof this.scene.objectScale === 'number' && this.scene.objectScale > 0) ? this.scene.objectScale : 1);
      this.scene.tweens.add({
        targets: sprite,
        y: sprite.y + dropDistance,
        alpha: 0,
        duration: FALL_DURATION_MS,
        ease: 'Cubic.easeIn',
        onComplete: () => {
          try { sprite.destroy(); } catch (_) {}
        }
      });
      return;
    }

    sprite.destroy();
  }

  reset() {
    this.arrows.forEach(arrow => {
      if (arrow.sprite) {
        try { arrow.sprite.destroy(); } catch (_) {}
      }
    });
    this.arrows = [];
  }
}

